import {Link, useLocation} from 'react-router-dom';
import {BookOpen, ExternalLink, LifeBuoy, Sparkles} from 'lucide-react';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  SidebarMenuBadge,
  SidebarMenuButton
} from '@kroll-resolver-crisp/ui-components-react';
import {useWhatsNew} from '@/api/hooks';
import {hasUnseenWhatsNew, WHATS_NEW_PATH} from '@/whatsNew/whatsNew';

// The Service Delivery space's PRI guides. The design system's in-app help is still an open item,
// so the Guides tile links out to them until it lands.
export const GUIDES_HREF =
  'https://crispthinking1.atlassian.net/wiki/spaces/SERVDEL/folder/3927506966';

// The Wellbeing dialog's tile, so the footer's dialogs read as one set.
const TILE =
  'border-border bg-card text-card-foreground hover:bg-accent hover:border-foreground/18 flex flex-col border p-5 text-left transition-colors';

function TileText({title, description}: {readonly title: string; readonly description: string}) {
  return (
    <>
      <span className="font-heading mb-1.5 text-sm leading-[1.3] font-medium tracking-[-0.01em] text-balance">
        {title}
      </span>
      <span className="text-muted-foreground text-xs leading-relaxed text-pretty">
        {description}
      </span>
    </>
  );
}

// Guides and What's new behind one footer row, as Wellbeing groups its links, so the footer stays
// short. The row carries the unread dot while there's a note the browser hasn't seen; until the notes
// have loaded, or if they can't be, there's no dot.
export function HelpDialog() {
  const {pathname} = useLocation();
  const {data} = useWhatsNew();
  const unseen = data ? hasUnseenWhatsNew(pathname, data.notes) : false;

  return (
    <Dialog>
      <SidebarMenuButton
        render={<DialogTrigger />}
        isActive={pathname === WHATS_NEW_PATH}
        tooltip="Help">
        <LifeBuoy />
        <span>Help</span>
      </SidebarMenuButton>
      {unseen ? (
        <SidebarMenuBadge aria-label="New changes" title="New changes">
          <span className="bg-primary size-2 rounded-full" />
        </SidebarMenuBadge>
      ) : null}
      <DialogContent className="sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Help</DialogTitle>
          <DialogDescription>How to use PRI, and what has changed in it.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {/* A new tab, as the Wellbeing links are: following it in place would leave the app and
              lose whatever the analyst was in the middle of. */}
          <a href={GUIDES_HREF} target="_blank" rel="noopener noreferrer" className={TILE}>
            <span className="mb-3 flex items-start justify-between">
              <BookOpen className="text-foreground size-[18px]" />
              <ExternalLink aria-hidden="true" className="text-muted-foreground size-3" />
            </span>
            <TileText title="Guides" description="Step-by-step guides to PRI on Confluence." />
            <span className="sr-only">(opens in a new tab)</span>
          </a>
          <DialogClose render={<Link to={WHATS_NEW_PATH} />} className={TILE}>
            <span className="mb-3 flex items-start justify-between">
              <Sparkles className="text-foreground size-[18px]" />
              {unseen ? (
                <span className="bg-primary size-2 rounded-full" aria-label="New changes" />
              ) : null}
            </span>
            <TileText
              title="What's new"
              description="Changes in the version of PRI you're using, and how to use them."
            />
          </DialogClose>
        </div>
      </DialogContent>
    </Dialog>
  );
}
